// src/services/users.service.js
import { http } from "./http";

/* =========================================
 * Helpers de organización (opcional)
 * ======================================= */
const ORG_ID = process.env.REACT_APP_ORG_ID || null;
const orgRef = () => (ORG_ID ? { id: ORG_ID } : undefined);

/* =========================================
 * Builders
 * ======================================= */
/**
 * Construye el payload para crear/actualizar un usuario.
 * Acepta tanto departmentId/roleIds como objetos completos.
 */
export function buildUserPayload(input = {}) {
  const {
    firstName,
    middleName,
    lastName,
    title,
    email,
    countryCode,
    phoneNumber,
    mobileNumber,
    manager,
    receiveQuestionnaires,
    camundaUser,
    departmentId,
    department,
    roleIds,
    roles,
  } = input;

  const dept =
    department ??
    (departmentId
      ? { id: departmentId, ...(ORG_ID ? { organization: orgRef() } : {}) }
      : undefined);

  const roleObjs =
    roles ??
    (Array.isArray(roleIds)
      ? roleIds.map((id) => ({ id, ...(ORG_ID ? { organization: orgRef() } : {}) }))
      : undefined);

  const base = {
    firstName,
    middleName,
    lastName,
    title,
    email,
    countryCode,
    phoneNumber,
    mobileNumber,
    manager,
    receiveQuestionnaires,
    camundaUser,
    department: dept,
    roles: roleObjs,
  };

  // elimina claves undefined
  Object.keys(base).forEach((k) => base[k] === undefined && delete base[k]);
  return base;
}

/* =========================================
 * CRUD básico
 * ======================================= */
export async function listUsers(params) {
  const { data } = await http.get("/users", { params });
  return data;
}

export async function getUser(id) {
  const { data } = await http.get(`/users/${id}`);
  return data;
}

export async function createUser(payload) {
  const body =
    payload && (payload.department || payload.departmentId || payload.roles || payload.roleIds)
      ? buildUserPayload(payload)
      : payload;

  const { data } = await http.post("/users", body);
  return data;
}

export async function updateUser(id, payload) {
  const body =
    payload && (payload.department || payload.departmentId || payload.roles || payload.roleIds)
      ? buildUserPayload(payload)
      : payload;

  const { data } = await http.put(`/users/${id}`, body);
  return data;
}

export async function deleteUser(id) {
  const { data } = await http.delete(`/users/${id}`);
  return data;
}

/* =========================================
 * Filtros útiles
 * ======================================= */
export async function listUsersByRole(roleId) {
  const { data } = await http.get(`/users/by-role/${roleId}`);
  return data;
}

export async function listUsersByDepartment(departmentId) {
  const { data } = await http.get(`/users/by-department/${departmentId}`);
  return data;
}

/* =========================================
 * /me y cambios de contraseña
 * ======================================= */
export async function getMe() {
  const { data } = await http.get("/users/me");
  return data;
}

/** Keep passwords in the request body, never in the URL. */
export async function changeMyPassword(password) {
  const { data } = await http.post("/users/me/changePassword", { password });
  return data;
}

/* =========================================
 * CSV Upload/Download helpers
 * ======================================= */

/**
 * Intenta extraer el nombre de archivo de un mensaje de texto del backend.
 * Busca el último segmento que sigue a "/api/users/download/".
 */
export function extractFileNameFromMessage(message) {
  if (typeof message !== "string") return null;
  const marker = "/api/users/download/";
  const idx = message.lastIndexOf(marker);
  if (idx === -1) return null;
  const tail = message.substring(idx + marker.length);
  const parts = tail.split(/[\s"'<>]/).filter(Boolean);
  return parts[0] || null;
}

/**
 * Construye una URL de descarga usando la baseURL del http client.
 */
export function buildDownloadUrl(fileName) {
  if (!fileName) return null;
  const base = http?.defaults?.baseURL || "";
  const prefix = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${prefix}/users/download/${encodeURIComponent(fileName)}`;
}

/**
 * Sube un CSV para crear usuarios.
 * Devuelve { message, fileName, downloadPath, downloadUrl }.
 */
export async function uploadUsersCSV(organizationId, file) {
  if (!organizationId) throw new Error("organizationId is required for CSV upload");
  if (!file) throw new Error("file is required");

  const form = new FormData();
  form.append("file", file, file.name); // el backend espera 'file'

  const res = await http.post(`/users/upload/${organizationId}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    transformRequest: (data) => data,
  });

  const message = typeof res?.data === "string" ? res.data : "";
  const fileName = extractFileNameFromMessage(message);
  const downloadUrl = buildDownloadUrl(fileName);

  return {
    message,
    fileName,
    downloadPath: fileName ? `/api/users/download/${fileName}` : null,
    downloadUrl,
  };
}

/**
 * Descarga el archivo de resultado como Blob.
 */
export async function downloadUploadResult(fileName) {
  if (!fileName) throw new Error("fileName is required");
  const { data } = await http.get(`/users/download/${encodeURIComponent(fileName)}`, {
    responseType: "blob",
  });
  return data;
}
